<template>
  <nav class="toolbar">
    <div class="toolbarMain">
      <IconButton
        class="toolbarSpacing"
        :data-test-id="$testId('addApplicationBtn')"
        :icon="$options.mainActionButtonIconName"
        iconPosition="left"
        :text="$options.mainActionButtonText"
        type="primary"
        @click="handleAddApplication"
      />
      <InputSearch
        class="search toolbarSpacing"
        :focus="shouldFocusSearch"
        :placeholder="$options.searchPlaceholder"
        :value="searchTerm"
        @input="triggerSearch"
      />
      <div class="rightContainer">
        <div class="appCount toolbarSpacing">
          <div :data-test-id="$testId('applicationCount')">
            {{ getApplicationCountText }}
          </div>
        </div>
        <IconButton
          class="toolbarSpacing"
          :data-test-id="$testId('exportMetadataBtn')"
          hasOutline
          icon="fileDownload"
          iconPosition="left"
          :isDisabled="isExportMetadataDisabled"
          text="Export Metadata"
          type="secondary"
          @click="handleExportMetadata"
        />
        <IconButton
          class="toolbarSpacing"
          :data-test-id="$testId('deleteApplicationBtn')"
          hasOutline
          icon="delete"
          iconPosition="left"
          :isDisabled="isDeleteAppDisabled"
          text="Delete"
          type="error"
          @click="handleDeleteApps"
        />
      </div>
    </div>
    <div class="toolbarSecondary">
      <RunReportButton
        v-if="canRunReport"
        :data-test-id="$testId('runReportBtn')"
        :reportType="reportType"
      />
    </div>
  </nav>
</template>

<script>
import * as Constants from 'client/util/Constants';
import {
  IconButton,
  InputSearch,
} from '@jumpcloud/ui-components';
import { UiEvents } from '@jumpcloud/ui-events-vue';
import { applicationSsoTypes } from 'client/util/Constants/ApplicationConstants';
import { eventLocations } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapGetters, mapState } from 'vuex';
import ReportMetadata from 'client/Reports/ReportMetadata';
import RunReportButton from 'client/Reports/components/RunReportButton';
import Util from 'client/util/Util';
import pluralize from 'pluralize';

const ApplicationCollection = 'ApplicationCollection';
const SettingsModule = 'SettingsModule';
const {
  ItemTypeConsts,
  ItemTypeRoutes,
} = Constants;
const { SAML } = applicationSsoTypes;
const route = ItemTypeRoutes[ItemTypeConsts.sso];
const searchPlaceholder = 'Search Configured Applications';

export default {
  name: 'ApplicationItemListToolbar',

  components: {
    IconButton,
    InputSearch,
    RunReportButton,
  },

  props: {
    reportType: {
      type: String,
      default: '',
    },
    searchTerm: {
      type: String,
      default: '',
    },
    shouldFocusSearch: Boolean,
  },

  mainActionButtonIconName: 'add',
  mainActionButtonText: 'Add New Application',
  route,
  searchPlaceholder,

  data() {
    return {
      deleteLoading: false,
      report: ReportMetadata[this.reportType],
    };
  },

  computed: {
    ...mapGetters(ApplicationCollection, {
      collection: 'getCollection',
      selectedItems: 'getSelectedItems',
      selectedItemsCount: 'getSelectedItemsCount',
      totalCount: 'getTotalCount',
      totalCountOfAllItems: 'getTotalCountOfAllItems',
    }),

    ...mapState(SettingsModule, [
      'orgId',
    ]),

    canRunReport() {
      return this.reportType
        && this.$hasFeatureFlag(this.$featureFlags.synthesizedReports)
        && this.report.isEnabled(this);
    },

    getApplicationCountText() {
      if (this.selectedItemsCount) {
        return `${this.selectedItemsCount} of ${this.totalCountOfAllItems} ${pluralize('Application', this.totalCountOfAllItems)} Selected`;
      }

      return `${this.totalCount ?? 0} ${pluralize('Application', this.totalCount)}`;
    },

    isDeleteAppDisabled() {
      return this.selectedItemsCount <= 0;
    },

    isExportMetadataDisabled() {
      // if only bookmarks are selected, disable export metadata button
      // if there are any saml apps selected, enable button
      return this.selectedItems.every(id => !this.checkCollectionForSaml(id));
    },
  },

  methods: {
    ...mapActions(ApplicationCollection, [
      'deleteItems',
    ]),

    checkCollectionForSaml(id) {
      return this.collection.some(obj => obj.id === id && obj.sso?.type === SAML);
    },

    handleAddApplication() {
      UiEvents.triggerIconClicked({
        description: 'Adds new Application',
        iconName: this.$options.mainActionButtonIconName,
        page: eventLocations.ssoPage,
        section: 'Applications Item List Action Bar',
        text: this.$options.mainActionButtonText,
      });

      Util.redirect(`${this.$options.route}/choose`);
    },

    handleDeleteApps() {
      this.$emit('deleteButtonClick');
    },

    handleExportMetadata() {
      this.selectedItems.forEach((id) => {
        // only sso apps pass conditional, no bookmarks
        if (this.checkCollectionForSaml(id)) {
          const path = `/api/organizations/${this.orgId}/applications/${id}/metadata.xml`;
          if (window && window.open) {
            window.open(path, '_blank');
          }
        }
      });
    },

    triggerSearch(value) {
      this.$emit('triggerSearch', value);
    },
  },
};
</script>

<style scoped>

.appCount {
  align-items: center;
  display: flex;
  font-weight: bold;
  justify-content: center;
}

.rightContainer {
  display: flex;
  flex: 1;
  justify-content: flex-end;
}

.search {
  flex: 0 1 45ch;
}

.toolbarMain {
  align-items: center;
  display: flex;
  padding: var(--jc-spacer) 0;
}

.toolbarSecondary {
  align-items: center;
  display: flex;
  justify-content: flex-end;
  padding: 0 40px 16px;
}

.toolbarSpacing {
  margin-right: 20px;
}
</style>
